// What the store offers for a card at each condition, each one asked AT that
// condition (TKT-2026-106839).
//
// The buylist pricing engine resolves its base price for the condition it is
// asked, and derives every other condition's NMFinalCash / LPFinalCash / ...
// key from that base. Only the asked condition's figure is its offer. The
// kiosk used to ask once (the proxy fills in NM) and read LP, MP, HP and DM out
// of that one NM answer, while the buylist's submit guard re-asks every line at
// its own condition. So an LP card went into the cart at the NM-derived figure,
// the guard measured it against the LP answer and refused the whole trade-in.
//
// Here every condition is asked in ONE bulk call, one item per condition, the
// same question the guard asks (buylist-api buildQuoteRequestBody), and each
// condition's figures are taken only from the answer to its own question. When
// an answer is missing that condition reads as not bought. It is never filled
// from another condition's answer, because that is the defect.
//
// The result keeps the flat shape the Sell To Us card and the detail dialog
// already read (`${cond}FinalCash`), carries the product-level fields
// (hotlist, darklist) from one answer, and keeps every answer whole under
// `byCondition` for the fields that belong to one condition.

import { fetchBulkBuylistPrices } from '../api/products';
import { BUYLIST_CONDITIONS } from './buylistCustomerDefaults';

const norm = (v) => (v == null ? '' : String(v).trim().toUpperCase());
const isOfferKey = (key) => key.endsWith('FinalCash') || key.endsWith('FinalCredit');

/** One bulk item per condition, for one product, printing and language. */
export function conditionQuoteItems({ productId, printing, language }, conditions = BUYLIST_CONDITIONS) {
    return conditions.map((condition) => ({ productId, printing, condition, language }));
}

/**
 * Fold the bulk answers into one price object whose `${cond}FinalCash` and
 * `${cond}FinalCredit` come from the answer asked at `cond`, and from nothing
 * else.
 *
 * @param {Array} prices     `prices` from POST /buylist-price/bulk
 * @param {number|string} productId  the product that was asked about
 * @param {string[]} [conditions]
 */
export function mergeConditionAnswers(prices, productId, conditions = BUYLIST_CONDITIONS) {
    const byCondition = {};
    for (const entry of Array.isArray(prices) ? prices : []) {
        if (!entry || !entry.price || typeof entry.price !== 'object') continue;
        if (String(entry.productId) !== String(productId)) continue;
        const cond = norm(entry.condition);
        if (cond && !byCondition[cond]) byCondition[cond] = entry.price;
    }

    const present = conditions.map(norm).filter((c) => byCondition[c]);
    if (present.length === 0) return null;

    // Product-level fields (hotlist, darklist, reference prices) from one
    // answer, with every per-condition offer it carried removed.
    const reference = byCondition.NM || byCondition[present[0]];
    const merged = {};
    for (const [key, value] of Object.entries(reference)) {
        if (!isOfferKey(key)) merged[key] = value;
    }

    for (const cond of conditions) {
        const answer = byCondition[norm(cond)];
        merged[`${cond}FinalCash`] = answer ? Number(answer[`${cond}FinalCash`]) || 0 : 0;
        merged[`${cond}FinalCredit`] = answer ? Number(answer[`${cond}FinalCredit`]) || 0 : 0;
    }
    merged.byCondition = byCondition;
    return merged;
}

/** The whole answer asked at `condition`, for fields that belong to one condition. */
export function quoteForCondition(quotes, condition) {
    return quotes?.byCondition?.[norm(condition)] || null;
}

/**
 * Ask the store's buylist what it offers for this product at every condition.
 * Resolves to the merged price object, or null when nothing came back.
 */
export async function fetchBuylistQuotesByCondition(storeName, { productId, printing, language }, conditions = BUYLIST_CONDITIONS) {
    const response = await fetchBulkBuylistPrices(storeName, conditionQuoteItems({ productId, printing, language }, conditions));
    return mergeConditionAnswers(response?.data?.prices, productId, conditions);
}
